import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    effect,
    inject,
    input,
    output,
} from '@angular/core';
import { type NotificationItem } from '../notification.model';

/**
 * Одна карточка стопки: текст, кнопка действия и крестик.
 *
 * Таймер авто-скрытия живёт здесь же: наведение мыши или фокус внутри карточки
 * останавливают отсчёт, чтобы сообщение можно было дочитать и нажать действие.
 */
@Component({
    selector: 'app-notification-card',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './notification-card.html',
    styleUrl: './notification-card.scss',
})
export class NotificationCard {
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);

    readonly notification = input.required<NotificationItem>();
    readonly dismissed = output<number>();

    private timer: ReturnType<typeof setTimeout> | null = null;
    private remaining = 0;
    private startedAt = 0;
    private running = false;
    private closed = false;
    private hovered = false;
    private focused = false;
    private scheduledRevision = -1;

    constructor() {
        effect(() => {
            const { duration, revision } = this.notification();

            // Повтор сообщения продлевает жизнь карточки — отсчёт начинается заново.
            if (revision === this.scheduledRevision) return;
            this.scheduledRevision = revision;
            this.schedule(duration);
        });

        this.destroyRef.onDestroy(() => this.stopTimer());
    }

    protected onHoverStart(): void {
        this.hovered = true;
        this.pause();
    }

    protected onHoverEnd(): void {
        this.hovered = false;
        this.resumeIfIdle();
    }

    protected onFocusIn(): void {
        this.focused = true;
        this.pause();
    }

    /** Фокус между элементами внутри карточки — не повод возобновлять отсчёт. */
    protected onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget;
        if (next instanceof Node && this.host.nativeElement.contains(next)) return;

        this.focused = false;
        this.resumeIfIdle();
    }

    protected close(): void {
        if (this.closed) return;

        this.closed = true;
        this.stopTimer();
        this.dismissed.emit(this.notification().id);
    }

    protected runAction(): void {
        const action = this.notification().action;
        this.close();
        action?.handler?.();
    }

    private pause(): void {
        if (!this.running) return;

        this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
        this.stopTimer();
    }

    /** Отсчёт продолжается, только когда карточка не под курсором и без фокуса. */
    private resumeIfIdle(): void {
        if (this.hovered || this.focused || this.running || this.closed) return;
        if (this.remaining <= 0) return;

        this.runTimer();
    }

    private schedule(duration: number): void {
        this.stopTimer();
        this.remaining = duration;
        if (duration <= 0 || this.hovered || this.focused) return;

        this.runTimer();
    }

    private runTimer(): void {
        this.running = true;
        this.startedAt = Date.now();
        this.timer = setTimeout(() => {
            this.running = false;
            this.close();
        }, this.remaining);
    }

    private stopTimer(): void {
        if (this.timer !== null) clearTimeout(this.timer);
        this.timer = null;
        this.running = false;
    }
}
